/**
 * CommercialsCard — the buyer's cost model.
 *
 * ThreadHeads / Mini Zali buy FINISHED GOODS from suppliers at a unit price;
 * they are not cut-and-sew makers. So the default surface is deliberately
 * lean, like pre-preparing a purchase order:
 *
 *   1. Unit cost (hero) — what the supplier charges per unit, with a switchable
 *      incoterm (FOB / EXW / DDP / CIF / FCA / DAP). Flows into a PO line via
 *      `agreed_price`.
 *   2. Landed (delivered) cost — optional single number.
 *   3. Retail -> margin — live GPM against landed cost if set, else unit cost.
 *   4. Sourcing terms — MOQ, sample price, proposed quantity.
 *   5. Itemise cost — the full cut-and-sew build-up (fabric formula, trim, CMT,
 *      decoration, packaging, freight, duty), collapsed and hidden by default.
 *      It stays fully editable so no data path is lost, and offers its
 *      `computeCommercials` ex-works / landed estimate as figures the buyer can
 *      copy into the unit / landed fields above.
 *
 * Card-level edit toggle (pencil in header): the whole sheet enters edit mode
 * together and saves in one transaction via the page's `updateField`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeCommercials, type CommercialsInput } from '@threadheads/shared';
import {
  ArrowUpToLine,
  CheckCircle2,
  ChevronDown,
  Circle,
  Loader2,
  Minus,
  Pencil,
  Save,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { INCOTERMS, type Incoterm } from '../utils/status-style';
import { cn } from '@/lib/utils';

export interface CommercialsProduct {
  // Cost build-up (itemise expander only).
  fabric_consumption: number | null;
  fabric_price_per_unit: number | null;
  fabric_wastage_pct: number | null;
  fabric_cost: number | null;
  trim_cost: number | null;
  cmt_cost: number | null;
  decoration_cost: number | null;
  packaging_cost: number | null;
  // Landed adders (itemise expander only).
  freight_cost: number | null;
  duty_pct: number | null;
  sample_price: number | null;
  costing_order_qty: number | null;
  // Buyer cost model.
  agreed_price: number | null;
  landed_unit_cost: number | null;
  // Price + margin.
  retail_price: number | null;
  target_margin_pct: number | null;
  // Sourcing terms.
  agreed_currency: string | null;
  incoterm: Incoterm | null;
  incoterm_location: string | null;
  moq: number | null;
  proposed_quantity: number | null;
  price_agreed_by_supplier: boolean;
  price_agreed_by_us: boolean;
  // Landed-cost FX estimate (server-computed). `landed_estimate_base` is
  // agreed_price × FX × (1 + shipping_buffer_pct/100), expressed in
  // `base_currency` (the org base, e.g. 'AUD'). Null when there is no agreed
  // price or the FX rate is unavailable.
  shipping_buffer_pct: number | null;
  base_currency: string;
  landed_estimate_base: number | null;
  landed_estimate_fx_rate: number | null;
}

export interface CommercialsUpdates {
  fabric_consumption: number | null;
  fabric_price_per_unit: number | null;
  fabric_wastage_pct: number | null;
  fabric_cost: number | null;
  trim_cost: number | null;
  cmt_cost: number | null;
  decoration_cost: number | null;
  packaging_cost: number | null;
  freight_cost: number | null;
  duty_pct: number | null;
  sample_price: number | null;
  costing_order_qty: number | null;
  agreed_price: number | null;
  landed_unit_cost: number | null;
  retail_price: number | null;
  target_margin_pct: number | null;
  agreed_currency: string | null;
  incoterm: Incoterm | null;
  incoterm_location: string | null;
  moq: number | null;
  proposed_quantity: number | null;
  price_agreed_by_supplier: boolean;
  price_agreed_by_us: boolean;
  /** Shipping buffer % that feeds the landed FX estimate (inline-editable). */
  shipping_buffer_pct: number | null;
}

interface Props {
  product: CommercialsProduct;
  canEdit: boolean;
  /** Partial so the inline shipping-buffer control can write a single field
   *  without going through the card-level edit transaction. */
  onSave: (updates: Partial<CommercialsUpdates>) => Promise<void>;
  /** Sum of every buy-plan cell (colour × size), computed by the page from the
   *  variants query. Drives the "N units ≈ …" projected buy cost line. */
  buyPlanGrandTotal: number;
}

const parseNum = (s: string): number | null => {
  if (!s.trim()) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const parseInt10 = (s: string): number | null => {
  if (!s.trim()) return null;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
};

const numStr = (v: number | null): string => (v == null ? '' : String(v));

/** FOB is the buyer's default incoterm — the unit cost is assumed FOB unless
 *  the buyer switches it. */
const DEFAULT_INCOTERM: Incoterm = 'FOB';

type FormState = {
  fabric_consumption: string;
  fabric_price_per_unit: string;
  fabric_wastage_pct: string;
  fabric_cost: string;
  trim_cost: string;
  cmt_cost: string;
  decoration_cost: string;
  packaging_cost: string;
  freight_cost: string;
  duty_pct: string;
  sample_price: string;
  costing_order_qty: string;
  agreed_price: string;
  landed_unit_cost: string;
  retail_price: string;
  target_margin_pct: string;
  agreed_currency: string;
  incoterm: string;
  incoterm_location: string;
  moq: string;
  proposed_quantity: string;
  price_agreed_by_supplier: boolean;
  price_agreed_by_us: boolean;
};

type NumericField = Exclude<
  keyof FormState,
  'agreed_currency' | 'incoterm' | 'incoterm_location' | 'price_agreed_by_supplier' | 'price_agreed_by_us'
>;

const buildForm = (p: CommercialsProduct): FormState => ({
  fabric_consumption: numStr(p.fabric_consumption),
  fabric_price_per_unit: numStr(p.fabric_price_per_unit),
  fabric_wastage_pct: numStr(p.fabric_wastage_pct),
  fabric_cost: numStr(p.fabric_cost),
  trim_cost: numStr(p.trim_cost),
  cmt_cost: numStr(p.cmt_cost),
  decoration_cost: numStr(p.decoration_cost),
  packaging_cost: numStr(p.packaging_cost),
  freight_cost: numStr(p.freight_cost),
  duty_pct: numStr(p.duty_pct),
  sample_price: numStr(p.sample_price),
  costing_order_qty: numStr(p.costing_order_qty),
  agreed_price: numStr(p.agreed_price),
  landed_unit_cost: numStr(p.landed_unit_cost),
  retail_price: numStr(p.retail_price),
  target_margin_pct: numStr(p.target_margin_pct),
  // FOB is priced in USD by default — buyers switch it only when the supplier
  // quotes in another currency.
  agreed_currency: p.agreed_currency ?? 'USD',
  incoterm: p.incoterm ?? '',
  incoterm_location: p.incoterm_location ?? '',
  moq: numStr(p.moq),
  proposed_quantity: numStr(p.proposed_quantity),
  price_agreed_by_supplier: p.price_agreed_by_supplier,
  price_agreed_by_us: p.price_agreed_by_us,
});

const formToInput = (f: FormState): CommercialsInput => ({
  fabricConsumption: parseNum(f.fabric_consumption),
  fabricPricePerUnit: parseNum(f.fabric_price_per_unit),
  fabricWastagePct: parseNum(f.fabric_wastage_pct),
  fabricCost: parseNum(f.fabric_cost),
  trimCost: parseNum(f.trim_cost),
  cmtCost: parseNum(f.cmt_cost),
  decorationCost: parseNum(f.decoration_cost),
  packagingCost: parseNum(f.packaging_cost),
  freightCost: parseNum(f.freight_cost),
  dutyPct: parseNum(f.duty_pct),
  samplePrice: parseNum(f.sample_price),
  costingOrderQty: parseInt10(f.costing_order_qty),
  targetMarginPct: parseNum(f.target_margin_pct),
  retailPrice: parseNum(f.retail_price),
  agreedPrice: parseNum(f.agreed_price),
});

/**
 * Map a product's stored commercials columns to the shared `computeCommercials`
 * input. Exported so other surfaces (e.g. the buy-plan grid's landed-value
 * line) derive their itemised landed estimate from the SAME mapping the
 * itemise expander uses.
 */
export const productToCommercialsInput = (p: CommercialsProduct): CommercialsInput => ({
  fabricConsumption: p.fabric_consumption,
  fabricPricePerUnit: p.fabric_price_per_unit,
  fabricWastagePct: p.fabric_wastage_pct,
  fabricCost: p.fabric_cost,
  trimCost: p.trim_cost,
  cmtCost: p.cmt_cost,
  decorationCost: p.decoration_cost,
  packagingCost: p.packaging_cost,
  freightCost: p.freight_cost,
  dutyPct: p.duty_pct,
  samplePrice: p.sample_price,
  costingOrderQty: p.costing_order_qty,
  targetMarginPct: p.target_margin_pct,
  retailPrice: p.retail_price,
  agreedPrice: p.agreed_price,
});

/** Format a value as currency; falls back to `12.34 CODE` if the code is not a
 *  valid ISO currency for Intl. */
const fmtCur = (v: number, code: string, dp = 2): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(v);
  } catch {
    return `${v.toFixed(dp)} ${code}`;
  }
};

/**
 * BufferPct — the inline-editable shipping-buffer percentage inside the landed
 * estimate line. Click-to-edit; commits on blur / Enter, cancels on Esc. Writes
 * a single `shipping_buffer_pct` field so it works in read mode too (the Finn
 * feature never requires entering the card's edit transaction).
 */
function BufferPct({
  value,
  canEdit,
  onCommit,
}: {
  value: number;
  canEdit: boolean;
  onCommit: (n: number | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    const next = trimmed === '' ? null : Math.min(100, Math.max(0, Number(trimmed)));
    if (trimmed !== '' && !Number.isFinite(next as number)) {
      setEditing(false);
      setDraft(String(value));
      return;
    }
    if ((next ?? null) === value || (next == null && value === 10)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onCommit(next);
      setEditing(false);
    } catch {
      // Keep editing so the user can retry; the parent toasts.
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return <span className="tabular-nums font-medium text-foreground/80">{value}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="tabular-nums font-medium text-threadheads-purple underline decoration-dotted underline-offset-2 hover:decoration-solid"
      >
        {value}
      </button>
    );
  }

  return (
    <input
      type="number"
      min={0}
      max={100}
      inputMode="decimal"
      value={draft}
      autoFocus
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setEditing(false);
          setDraft(String(value));
        }
      }}
      className="h-5 w-11 rounded border border-input bg-background px-1 text-right text-[11px] tabular-nums focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    />
  );
}

export function CommercialsCard({ product, canEdit, onSave, buyPlanGrandTotal }: Props) {
  const { t } = useTranslation('productDev');
  const { organization } = useOrganization();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(() => buildForm(product));
  const [saving, setSaving] = useState(false);
  const [itemiseOpen, setItemiseOpen] = useState(false);

  const currency =
    (editing ? form.agreed_currency.trim() : product.agreed_currency ?? '') ||
    organization?.default_currency ||
    'USD';

  // Itemise-expander estimate (the old cut-and-sew build-up).
  const itemiseInput = useMemo<CommercialsInput>(
    () => (editing ? formToInput(form) : productToCommercialsInput(product)),
    [editing, form, product],
  );
  const c = useMemo(() => computeCommercials(itemiseInput), [itemiseInput]);

  // Money (2dp, tabular). `mc` appends the currency code for headline figures.
  const m2 = (v: number | null): string => (v == null ? '-' : v.toFixed(2));
  const mc = (v: number | null): string => (v == null ? '-' : `${v.toFixed(2)} ${currency}`);
  const pctFmt = (v: number | null, dp = 1): string => (v == null ? '-' : `${v.toFixed(dp)}%`);

  // ---- buyer cost model (default surface) -----------------------------------
  // Live values track the form while editing, the product otherwise.
  const unitCost = editing ? parseNum(form.agreed_price) : product.agreed_price;
  const landedCost = editing ? parseNum(form.landed_unit_cost) : product.landed_unit_cost;
  const retail = editing ? parseNum(form.retail_price) : product.retail_price;
  const targetMargin = editing ? parseNum(form.target_margin_pct) : product.target_margin_pct;
  const incotermLabel = (editing ? form.incoterm : product.incoterm) || DEFAULT_INCOTERM;

  // Margin basis, best available: manual landed override, else the server's
  // FX-adjusted landed estimate, else the raw unit cost.
  const landedEstBase = product.landed_estimate_base;
  const basis = landedCost ?? landedEstBase ?? unitCost;
  const basisKind: 'landed' | 'landedEst' | 'unit' =
    landedCost != null ? 'landed' : landedEstBase != null ? 'landedEst' : 'unit';
  const basisLabel =
    basisKind === 'landed'
      ? t('commercials.marginVsLanded')
      : basisKind === 'landedEst'
        ? t('commercials.marginVsLandedEst')
        : t('commercials.marginVsUnit');

  // Landed estimate line (the "Finn" feature): the server figure in base
  // currency + the inline-editable shipping buffer. Reads from `product` (the
  // saved values the estimate was computed from), not the live form.
  const bufferPct = product.shipping_buffer_pct ?? 10;
  const saveBuffer = (n: number | null) => onSave({ shipping_buffer_pct: n });

  // Projected buy cost = grand total × the same basis as the margin. When the
  // basis is the raw FOB unit cost we convert with the estimate's FX rate if we
  // have one, otherwise we stay honest and show it in the FOB currency.
  const projected = useMemo<{ amount: number; code: string } | null>(() => {
    if (buyPlanGrandTotal <= 0 || basis == null) return null;
    if (basisKind === 'landed') return { amount: buyPlanGrandTotal * basis, code: currency };
    if (basisKind === 'landedEst')
      return { amount: buyPlanGrandTotal * basis, code: product.base_currency };
    const fx = product.landed_estimate_fx_rate;
    return fx != null
      ? { amount: buyPlanGrandTotal * basis * fx, code: product.base_currency }
      : { amount: buyPlanGrandTotal * basis, code: currency };
  }, [
    buyPlanGrandTotal,
    basis,
    basisKind,
    currency,
    product.base_currency,
    product.landed_estimate_fx_rate,
  ]);

  // GPM = (retail - basis) / retail. Suggested price = basis / (1 - margin%).
  const grossMarginPct =
    retail != null && retail > 0 && basis != null
      ? Math.round(((retail - basis) / retail) * 1000) / 10
      : null;
  const suggestedPrice =
    targetMargin != null && targetMargin < 100 && basis != null && basis > 0
      ? Math.round((basis / (1 - targetMargin / 100)) * 100) / 100
      : null;
  const marginHolds =
    grossMarginPct != null && targetMargin != null ? grossMarginPct >= targetMargin : null;

  // Subordinate planning line: states the target-margin → suggested-price
  // relationship in words so retail stays the single authoritative price and
  // the suggested price never reads as a second competing headline. Shared by
  // read + edit modes.
  const planningLine: React.ReactNode = (() => {
    if (targetMargin == null || suggestedPrice == null) return null;
    const pct = pctFmt(targetMargin);
    const price = mc(suggestedPrice);
    if (retail == null) {
      return t('commercials.planning.noRetail', { pct, price });
    }
    const delta = Math.round((retail - suggestedPrice) * 100) / 100;
    if (Math.abs(delta) < 0.005) {
      return t('commercials.planning.onTarget', { pct, price });
    }
    const amount = mc(Math.abs(delta));
    // delta > 0 → suggested price sits BELOW retail (margin holds comfortably).
    return delta > 0
      ? t('commercials.planning.below', { pct, price, amount })
      : t('commercials.planning.above', { pct, price, amount });
  })();

  const startEdit = () => {
    setForm(buildForm(product));
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const commit = async () => {
    setSaving(true);
    try {
      await onSave({
        fabric_consumption: parseNum(form.fabric_consumption),
        fabric_price_per_unit: parseNum(form.fabric_price_per_unit),
        fabric_wastage_pct: parseNum(form.fabric_wastage_pct),
        fabric_cost: parseNum(form.fabric_cost),
        trim_cost: parseNum(form.trim_cost),
        cmt_cost: parseNum(form.cmt_cost),
        decoration_cost: parseNum(form.decoration_cost),
        packaging_cost: parseNum(form.packaging_cost),
        freight_cost: parseNum(form.freight_cost),
        duty_pct: parseNum(form.duty_pct),
        sample_price: parseNum(form.sample_price),
        costing_order_qty: parseInt10(form.costing_order_qty),
        agreed_price: parseNum(form.agreed_price),
        landed_unit_cost: parseNum(form.landed_unit_cost),
        retail_price: parseNum(form.retail_price),
        target_margin_pct: parseNum(form.target_margin_pct),
        agreed_currency: form.agreed_currency.trim()
          ? form.agreed_currency.trim().toUpperCase()
          : null,
        incoterm: (form.incoterm || DEFAULT_INCOTERM) as Incoterm,
        incoterm_location: form.incoterm_location.trim() || null,
        moq: parseInt10(form.moq),
        proposed_quantity: parseInt10(form.proposed_quantity),
        price_agreed_by_supplier: form.price_agreed_by_supplier,
        price_agreed_by_us: form.price_agreed_by_us,
      });
      setEditing(false);
    } catch (err) {
      toast({
        title: t('commercials.toast.saveFailed'),
        description: err instanceof Error ? err.message : t('commercials.toast.tryAgain'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const allEmpty =
    product.agreed_price == null &&
    product.landed_unit_cost == null &&
    product.retail_price == null &&
    product.target_margin_pct == null &&
    product.moq == null &&
    product.proposed_quantity == null &&
    product.sample_price == null &&
    product.incoterm == null &&
    product.fabric_consumption == null &&
    product.fabric_price_per_unit == null &&
    product.fabric_wastage_pct == null &&
    product.fabric_cost == null &&
    product.trim_cost == null &&
    product.cmt_cost == null &&
    product.decoration_cost == null &&
    product.packaging_cost == null &&
    product.freight_cost == null &&
    product.duty_pct == null &&
    product.costing_order_qty == null &&
    !product.price_agreed_by_supplier &&
    !product.price_agreed_by_us;

  const hasBuildUp =
    product.fabric_consumption != null ||
    product.fabric_price_per_unit != null ||
    product.fabric_cost != null ||
    product.trim_cost != null ||
    product.cmt_cost != null ||
    product.decoration_cost != null ||
    product.packaging_cost != null ||
    product.freight_cost != null ||
    product.duty_pct != null;

  // ---- shared render helpers (function calls, not components, so inputs keep focus) ----

  const sectionTitle = (labelKey: string) => (
    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {t(labelKey)}
    </div>
  );

  const numInput = (
    key: NumericField,
    opts: { suffix?: string; placeholder?: string; step?: string } = {},
  ) => {
    const id = `comm-${key}`;
    return (
      <div className="relative">
        <Input
          id={id}
          type="number"
          min={0}
          step={opts.step ?? '0.01'}
          inputMode="decimal"
          value={form[key]}
          placeholder={opts.placeholder}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className={cn('h-8 text-sm text-right tabular-nums', opts.suffix && 'pr-7')}
        />
        {opts.suffix && (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {opts.suffix}
          </span>
        )}
      </div>
    );
  };

  const numRow = (
    key: NumericField,
    labelKey: string,
    opts: { suffix?: string; placeholder?: string; step?: string; hint?: React.ReactNode } = {},
  ) => (
    <div className="space-y-1">
      <div className="grid grid-cols-[1fr_9rem] items-center gap-3">
        <Label htmlFor={`comm-${key}`} className="text-xs text-muted-foreground">
          {t(labelKey)}
        </Label>
        {numInput(key, opts)}
      </div>
      {opts.hint && <p className="text-[11px] text-muted-foreground text-right">{opts.hint}</p>}
    </div>
  );

  // Stacked field (label above input) — keeps the sourcing-terms grid on a
  // uniform rhythm so both columns align regardless of label length.
  const stackedNumField = (
    key: NumericField,
    labelKey: string,
    opts: { suffix?: string; placeholder?: string; step?: string } = {},
  ) => (
    <div className="space-y-1">
      <Label htmlFor={`comm-${key}`} className="text-xs text-muted-foreground">
        {t(labelKey)}
      </Label>
      {numInput(key, opts)}
    </div>
  );

  const computedRow = (
    label: React.ReactNode,
    value: React.ReactNode,
    opts: { emphasis?: boolean; muted?: boolean } = {},
  ) => (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className={cn(
          'text-xs',
          opts.emphasis ? 'font-medium text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums',
          opts.emphasis ? 'text-base font-semibold' : 'text-sm font-medium',
          opts.muted && 'text-muted-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );

  const fabricBadge = (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      {c.fabricFromFormula ? t('commercials.badgeFormula') : t('commercials.badgeFlat')}
    </span>
  );

  const varianceChip = () => {
    if (c.quoteVariance == null) return null;
    const above = c.quoteVariance > 0.005;
    const below = c.quoteVariance < -0.005;
    const even = !above && !below;
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium tabular-nums',
          even && 'bg-muted text-muted-foreground',
          above && 'bg-amber-100 text-amber-800',
          below && 'bg-emerald-100 text-emerald-800',
        )}
      >
        {above ? (
          <TrendingUp className="h-3.5 w-3.5" />
        ) : below ? (
          <TrendingDown className="h-3.5 w-3.5" />
        ) : (
          <Minus className="h-3.5 w-3.5" />
        )}
        {c.quoteVariance >= 0 ? '+' : ''}
        {c.quoteVariance.toFixed(2)} {currency}
        <span className="text-[11px] font-normal opacity-80">
          {even
            ? t('commercials.quoteEven')
            : above
            ? t('commercials.quoteAbove')
            : t('commercials.quoteBelow')}
        </span>
      </span>
    );
  };

  // Gross-margin figure with health colour + trend glyph (shared read / edit).
  const marginFigure = (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 tabular-nums',
        marginHolds == null
          ? 'text-foreground'
          : marginHolds
          ? 'text-emerald-700'
          : 'text-amber-700',
      )}
    >
      {marginHolds != null &&
        (marginHolds ? (
          <TrendingUp className="h-3.5 w-3.5" />
        ) : (
          <TrendingDown className="h-3.5 w-3.5" />
        ))}
      {pctFmt(grossMarginPct)}
    </span>
  );

  // Landed FX estimate + projected buy cost. Renders identically in read / edit
  // (the estimate is server-computed from the SAVED agreed price); only shows
  // when there is an agreed price to estimate from.
  const estimateBlock =
    product.agreed_price == null ? null : (
      <div className="space-y-1">
        {landedEstBase != null ? (
          <div className="flex flex-wrap items-center text-[11px] text-muted-foreground">
            <span aria-hidden className="mr-1">
              ≈
            </span>
            <span className="mr-1 tabular-nums font-medium text-foreground/80">
              {fmtCur(landedEstBase, product.base_currency)}
            </span>
            <span className="mr-1">{t('commercials.landedEstNote')}</span>
            <BufferPct value={bufferPct} canEdit={canEdit} onCommit={saveBuffer} />
            <span>{t('commercials.landedEstSuffix')}</span>
          </div>
        ) : (
          <p className="text-[11px] italic text-muted-foreground">
            {t('commercials.fxUnavailable')}
          </p>
        )}
        {projected && (
          <p className="text-xs tabular-nums text-muted-foreground">
            {t('commercials.projectedBuyCost', {
              count: buyPlanGrandTotal,
              amount: fmtCur(projected.amount, projected.code, 0),
            })}
          </p>
        )}
      </div>
    );

  // The itemise build-up expander — editable inputs so no data path is lost,
  // plus its computeCommercials ex-works / landed estimate the buyer can copy
  // into the unit / landed fields above.
  const itemiseExpander = (
    <Collapsible open={itemiseOpen} onOpenChange={setItemiseOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border border-border/60 px-3 py-2 text-left transition-colors hover:bg-muted/40">
        <span className="text-xs font-medium text-muted-foreground">
          {t('commercials.itemise')}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none',
            itemiseOpen && 'rotate-180',
          )}
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="space-y-5 pt-3">
          <p className="text-[11px] text-muted-foreground">{t('commercials.itemiseHint')}</p>

          {/* Cost build-up */}
          <div className="space-y-2.5">
            {sectionTitle('commercials.section.costBuildUp')}
            <div className="rounded-md border border-border/60 p-3 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">{t('commercials.fabric')}</span>
                {fabricBadge}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="comm-fabric_consumption" className="text-[11px] text-muted-foreground">
                    {t('commercials.fabricConsumption')}
                  </Label>
                  {numInput('fabric_consumption', { step: '0.001' })}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="comm-fabric_price_per_unit" className="text-[11px] text-muted-foreground">
                    {t('commercials.fabricPricePerUnit')}
                  </Label>
                  {numInput('fabric_price_per_unit')}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="comm-fabric_wastage_pct" className="text-[11px] text-muted-foreground">
                    {t('commercials.fabricWastage')}
                  </Label>
                  {numInput('fabric_wastage_pct', { suffix: '%', step: '0.1' })}
                </div>
              </div>
              {computedRow(t('commercials.fabricLine'), m2(c.fabricLine), { emphasis: false })}
              <div className="grid grid-cols-[1fr_9rem] items-center gap-3 pt-1 border-t border-border/50">
                <Label htmlFor="comm-fabric_cost" className="text-[11px] text-muted-foreground">
                  {t('commercials.flatFabric')}
                </Label>
                {numInput('fabric_cost')}
              </div>
              <p className="text-[11px] text-muted-foreground">{t('commercials.flatFabricHint')}</p>
            </div>

            {numRow('trim_cost', 'commercials.trim')}
            {numRow('cmt_cost', 'commercials.cmt')}
            {numRow('decoration_cost', 'commercials.decoration')}
            {numRow('packaging_cost', 'commercials.packaging')}

            <div className="border-t border-border/60 pt-2">
              {computedRow(t('commercials.estimatedExWorks'), mc(c.exWorks), { emphasis: true })}
              <div className="flex justify-end pt-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setForm((f) => ({ ...f, agreed_price: c.exWorks ? String(c.exWorks) : '' }))
                  }
                  className="h-6 px-2 text-[11px] text-threadheads-purple hover:text-threadheads-purple"
                >
                  <ArrowUpToLine className="h-3 w-3 mr-1" /> {t('commercials.useAsUnitCost')}
                </Button>
              </div>
            </div>
          </div>

          {/* Landed band */}
          <div className="space-y-2.5">
            {sectionTitle('commercials.section.landed')}
            {computedRow(t('commercials.estimatedExWorks'), m2(c.exWorks), { muted: true })}
            {numRow('freight_cost', 'commercials.freight')}
            {numRow('duty_pct', 'commercials.duty', {
              suffix: '%',
              step: '0.1',
              hint: t('commercials.dutyComputed', { amount: m2(c.duty) }),
            })}
            {numRow('costing_order_qty', 'commercials.orderQty', {
              step: '1',
              placeholder:
                product.proposed_quantity != null ? String(product.proposed_quantity) : undefined,
              hint:
                product.proposed_quantity != null
                  ? t('commercials.orderQtyHint', { count: product.proposed_quantity })
                  : undefined,
            })}
            {computedRow(t('commercials.sampleAmortisation'), m2(c.sampleAmortisation), {
              muted: true,
            })}
            <div className="border-t border-border/60 pt-2">
              {computedRow(t('commercials.estimatedLanded'), mc(c.landed), { emphasis: true })}
              <div className="flex justify-end pt-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      landed_unit_cost: c.landed ? String(c.landed) : '',
                    }))
                  }
                  className="h-6 px-2 text-[11px] text-threadheads-purple hover:text-threadheads-purple"
                >
                  <ArrowUpToLine className="h-3 w-3 mr-1" /> {t('commercials.useAsLandedCost')}
                </Button>
              </div>
            </div>
          </div>

          {/* Unit cost vs the itemised estimate */}
          {c.quoteVariance != null && (
            <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-2.5">
              <span className="text-xs text-muted-foreground">
                {t('commercials.quoteVsExWorks')}
              </span>
              {varianceChip()}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">{t('commercials.title')}</CardTitle>
          {currency && (
            <span className="text-xs font-mono text-muted-foreground">{currency}</span>
          )}
        </div>
        {canEdit && !editing && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={startEdit}
            className="h-7 px-2 text-xs"
          >
            <Pencil className="h-3 w-3 mr-1" /> {t('commercials.edit')}
          </Button>
        )}
      </CardHeader>

      <CardContent>
        {editing ? (
          <div className="space-y-5">
            {/* 1. Unit cost (hero) — amount + currency + incoterm ---------------- */}
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3.5 space-y-2.5">
              {sectionTitle('commercials.unitCost')}
              <div className="flex items-end gap-2.5">
                <div className="relative flex-1">
                  <Input
                    id="comm-agreed_price"
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    value={form.agreed_price}
                    onChange={(e) => setForm((f) => ({ ...f, agreed_price: e.target.value }))}
                    className="h-11 text-xl font-semibold tabular-nums"
                  />
                </div>
                <div className="w-[4.75rem] space-y-1">
                  <Label htmlFor="comm-agreed_currency" className="sr-only">
                    {t('commercials.currency')}
                  </Label>
                  <Input
                    id="comm-agreed_currency"
                    maxLength={3}
                    placeholder="USD"
                    value={form.agreed_currency}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, agreed_currency: e.target.value.toUpperCase() }))
                    }
                    className="h-11 text-center font-mono uppercase tabular-nums"
                  />
                </div>
                <div className="w-[7rem] space-y-1">
                  <Label htmlFor="comm-incoterm" className="sr-only">
                    {t('commercials.incoterm')}
                  </Label>
                  <Select
                    value={form.incoterm || DEFAULT_INCOTERM}
                    onValueChange={(v) => setForm((f) => ({ ...f, incoterm: v }))}
                  >
                    <SelectTrigger id="comm-incoterm" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INCOTERMS.map((it) => (
                        <SelectItem key={it} value={it}>
                          {it}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">{t('commercials.unitCostHint')}</p>
            </div>

            {/* 2. Landed (delivered) cost, optional ------------------------------ */}
            <div className="space-y-1">
              {numRow('landed_unit_cost', 'commercials.landedOptional')}
              <p className="text-[11px] text-muted-foreground text-right">
                {t('commercials.landedHint')}
              </p>
            </div>

            {/* Landed FX estimate + projected buy cost --------------------------- */}
            {estimateBlock}

            {/* 3. Retail -> margin ----------------------------------------------- */}
            <div className="space-y-2.5">
              {sectionTitle('commercials.section.priceMargin')}
              {numRow('retail_price', 'commercials.retailPrice')}
              {numRow('target_margin_pct', 'commercials.targetMargin', {
                suffix: '%',
                step: '0.1',
              })}
              {grossMarginPct != null && (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-xs font-medium text-foreground">
                    {t('commercials.grossMargin')}
                    <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                      {basisLabel}
                    </span>
                  </span>
                  <span className="text-sm font-semibold">{marginFigure}</span>
                </div>
              )}
              {planningLine && (
                <p className="text-[11px] leading-snug text-muted-foreground">
                  {planningLine}
                </p>
              )}
            </div>

            {/* 4. Sourcing terms — one tidy stacked 2-column grid ---------------- */}
            <div className="space-y-2.5">
              {sectionTitle('commercials.section.sourcing')}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {stackedNumField('moq', 'commercials.moqUnits', { step: '1' })}
                {stackedNumField('sample_price', 'commercials.samplePrice')}
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="comm-incoterm_location" className="text-xs text-muted-foreground">
                    {t('commercials.incotermLocation')}
                  </Label>
                  <Input
                    id="comm-incoterm_location"
                    className="h-8 text-sm max-w-prose"
                    placeholder={t('commercials.incotermLocationPlaceholder')}
                    value={form.incoterm_location}
                    onChange={(e) => setForm((f) => ({ ...f, incoterm_location: e.target.value }))}
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={form.price_agreed_by_supplier}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, price_agreed_by_supplier: v === true }))
                    }
                  />
                  {t('commercials.supplierConfirmed')}
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox
                    checked={form.price_agreed_by_us}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, price_agreed_by_us: v === true }))
                    }
                  />
                  {t('commercials.weConfirmed')}
                </label>
              </div>
            </div>

            {/* 5. Itemise cost (collapsed) --------------------------------------- */}
            {itemiseExpander}

            {/* Actions ----------------------------------------------------------- */}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={cancel}
                disabled={saving}
                className="h-7 px-2 text-xs"
              >
                <X className="h-3 w-3 mr-1" /> {t('commercials.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={commit}
                disabled={saving}
                className="h-7 px-3 text-xs bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              >
                {saving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Save className="h-3 w-3 mr-1" /> {t('commercials.save')}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : allEmpty ? (
          <p className="text-sm text-muted-foreground">
            {canEdit ? t('commercials.emptyEditable') : t('commercials.empty')}
          </p>
        ) : (
          // Read mode — tidy buyer summary.
          <div className="space-y-4">
            {/* Unit cost hero */}
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('commercials.unitCost')}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  <span className="font-mono font-medium">{incotermLabel}</span>
                  {product.incoterm_location && <span>{product.incoterm_location}</span>}
                </span>
              </div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {mc(product.agreed_price)}
              </div>
            </div>

            {/* Landed FX estimate + projected buy cost */}
            {estimateBlock}

            <dl className="grid grid-cols-[150px_1fr] gap-x-4 gap-y-2.5 text-sm">
              {product.landed_unit_cost != null && (
                <>
                  <dt className="text-muted-foreground">{t('commercials.landedCost')}</dt>
                  <dd className="text-right font-semibold tabular-nums">
                    {mc(product.landed_unit_cost)}
                  </dd>
                </>
              )}

              <dt className="text-muted-foreground">{t('commercials.retailPrice')}</dt>
              <dd className="text-right font-medium tabular-nums">{mc(product.retail_price)}</dd>

              <dt className="text-muted-foreground">
                {t('commercials.grossMargin')}
                <span className="ml-1.5 text-[11px] text-muted-foreground/80">{basisLabel}</span>
              </dt>
              <dd className="text-right font-medium">
                <span className="inline-flex justify-end">{marginFigure}</span>
              </dd>

              {planningLine && (
                <p className="col-span-2 text-[11px] leading-snug text-muted-foreground">
                  {planningLine}
                </p>
              )}
            </dl>

            <dl className="grid grid-cols-[150px_1fr] gap-x-4 gap-y-2.5 text-sm border-t pt-3">
              <dt className="text-muted-foreground">{t('commercials.moq')}</dt>
              <dd className="text-right font-medium tabular-nums">
                {product.moq != null ? t('commercials.unitsCount', { count: product.moq }) : '-'}
              </dd>

              <dt className="text-muted-foreground">{t('commercials.confirmedBy')}</dt>
              <dd className="flex items-center justify-end gap-4">
                <span
                  className={cn(
                    'inline-flex items-center gap-1',
                    product.price_agreed_by_supplier
                      ? 'text-emerald-700'
                      : 'text-muted-foreground/60',
                  )}
                >
                  {product.price_agreed_by_supplier ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <Circle className="h-3.5 w-3.5" />
                  )}
                  {t('commercials.supplier')}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1',
                    product.price_agreed_by_us ? 'text-emerald-700' : 'text-muted-foreground/60',
                  )}
                >
                  {product.price_agreed_by_us ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <Circle className="h-3.5 w-3.5" />
                  )}
                  {t('commercials.us')}
                </span>
              </dd>
            </dl>

            {/* Itemise cost — collapsed read-only estimate, only when build-up
                data exists. */}
            {hasBuildUp && (
              <Collapsible open={itemiseOpen} onOpenChange={setItemiseOpen}>
                <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border border-border/60 px-3 py-2 text-left transition-colors hover:bg-muted/40">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('commercials.itemise')}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none',
                      itemiseOpen && 'rotate-180',
                    )}
                    aria-hidden
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                  <dl className="grid grid-cols-[150px_1fr] gap-x-4 gap-y-2 text-sm pt-3">
                    <dt className="text-muted-foreground">{t('commercials.estimatedExWorks')}</dt>
                    <dd className="text-right font-medium tabular-nums">{mc(c.exWorks)}</dd>
                    <dt className="text-muted-foreground">{t('commercials.estimatedLanded')}</dt>
                    <dd className="text-right font-semibold tabular-nums">{mc(c.landed)}</dd>
                  </dl>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
